import React from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useCredentials, Credential as APICredential } from '../../hooks/useCredentials';
import { useTemplate } from '../../hooks/useTemplate';
import config from '../../config/environment';
import { CredentialPreview } from '../credentials/CredentialPDF';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { 
  Shield, 
  Share2, 
  Download, 
  Eye,
  LogOut,
  Mail,
  Link,
  Bell,
  FileText,
  ExternalLink,
  AlertTriangle,
  RefreshCw
} from 'lucide-react';

export default function RecipientDashboard() {
  const { user, logout } = useAuth();
  const { credentials, loading, error, refreshCredentials } = useCredentials();
  
  // Get template data for the first credential (we'll enhance this to handle multiple)
  const firstCredential = credentials[0];
  const { template: realTemplate, loading: templateLoading, error: templateError } = useTemplate(
    firstCredential?.template_file_id, 
    firstCredential?.schema_id
  );
  
  // Removed unused state variables and effects since we're only showing credentials list

  const generateShareUrl = (credentialId: string) => {
    return `https://credentialcapital.co/verify/${credentialId}`;
  };

  const generateQRCode = (url: string) => {
    // In a real implementation, this would generate an actual QR code
    return `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(url)}`;
  };

  const downloadCredential = async (credentialId: string) => {
    try {
      const token = localStorage.getItem('jwt_token');
      if (!token) {
        alert('No authentication token found. Please log in.');
        return;
      }

      const response = await fetch(`${config.REACT_APP_API_BASE_URL}/api/credentials/${credentialId}/download`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Download failed: ${response.statusText}`);
      }

      // Create blob and download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `credential_${credentialId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Download error:', error);
      alert('Failed to download credential. Please try again.');
    }
  };

  const openInNewTab = (credentialId: string) => {
    console.log('Opening credential in new tab:', credentialId);
    const verificationUrl = `https://credentialcapital.co/verify/${credentialId}`;
    window.open(verificationUrl, '_blank');
  };

  const shareOnLinkedIn = (credentialId: string) => {
    console.log('Sharing credential on LinkedIn:', credentialId);
    const verificationUrl = `https://credentialcapital.co/verify/${credentialId}`;
    const linkedInUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(verificationUrl)}`;
    window.open(linkedInUrl, '_blank');
  };

  // Get the appropriate template for a credential
  const getCredentialTemplate = (credential) => {
    // Only use real template data from API
    if (credential.template_file_id) {
      // Return a basic template structure based on API data
      return {
        id: credential.template_file_id,
        name: credential.schema_name || 'Credential Template',
        width: 800,
        height: 600,
        backgroundColor: '#ffffff',
        fields: [] // Will be populated by the template hook
      };
    }
    
    // No template available
    return null;
  };

  // Transform API credentials to match the expected format
  const transformedCredentials = credentials.map((credential: APICredential) => ({
    id: credential.id,
    title: credential.fields.student_name || credential.fields.course_name || credential.schema_name,
    issuer: credential.issuer_name,
    issuerLogo: '🎓', // Default emoji for now
    issuedDate: new Date(credential.issued_at).toLocaleDateString(),
    expiryDate: null, // API doesn't provide expiry date yet
    status: credential.status === 'issued' ? 'active' : credential.status,
    category: 'Education', // Default category based on our test data
    verificationCount: 0, // API doesn't provide verification count yet
    lastVerified: new Date(credential.issued_at).toLocaleDateString(),
    fields: credential.fields,
    templateId: credential.schema_id, // Use schema ID to find template
    schemaId: credential.schema_id,
    template_file_id: credential.template_file_id // Include template file ID from API
  }));

    // Since we removed filters, we'll use all credentials directly
  const filteredCredentials = transformedCredentials;

  {/* Activity logs and stats - COMMENTED OUT since activity section is commented out */}
  /*
  // Generate comprehensive activity logs for recipient
  const generateRecipientActivityLogs = () => {
    const now = new Date();
    const logs = [];

    // Policy evaluation logs
    logs.push(
      { id: '1', timestamp: new Date(now.getTime() - 5 * 60 * 1000), type: 'policy_evaluation', category: 'Healthcare', policyName: 'Medical Staff Access Policy', credentialTitle: 'Medical License', result: 'success', details: 'Policy evaluation successful - All requirements met for hospital access', location: 'City Hospital Emergency Department', verifier: 'Dr. Sarah Chen, MD', responseTime: 1.2 },
      { id: '2', timestamp: new Date(now.getTime() - 12 * 60 * 1000), type: 'agent_verification', category: 'Education', agentName: 'Student Event Check-In', credentialTitle: 'Student ID', result: 'success', details: 'Successfully verified for Spring Career Fair access', location: 'University Student Union', verifier: 'Career Services', responseTime: 0.8 },
      { id: '3', timestamp: new Date(now.getTime() - 23 * 60 * 1000), type: 'policy_evaluation', category: 'Real Estate', policyName: 'Broker Licensing Verification', credentialTitle: 'Real Estate License', result: 'failed', details: 'Policy evaluation failed - License requires renewal before property closing', location: 'Downtown Title Office', verifier: 'Metro Title Services', error: 'License Expiration', responseTime: 2.1 },
      { id: '4', timestamp: new Date(now.getTime() - 35 * 60 * 1000), type: 'agent_verification', category: 'Healthcare', agentName: 'Medical Staff Shift Check-In', credentialTitle: 'Nursing License', result: 'success', details: 'Night shift access granted to ICU department', location: 'City Hospital ICU', verifier: 'Nurse Manager', responseTime: 1.4 },
      { id: '5', timestamp: new Date(now.getTime() - 47 * 60 * 1000), type: 'credential_shared', category: 'Education', credentialTitle: 'Degree Certificate', result: 'success', details: 'Credential shared with potential employer for background verification', location: 'Online Portal', verifier: 'TechCorp HR Department', sharedVia: 'Secure Link' },
      { id: '6', timestamp: new Date(now.getTime() - 58 * 60 * 1000), type: 'policy_evaluation', category: 'Livestock', policyName: 'Handler Safety Compliance', credentialTitle: 'Handler Certification', result: 'success', details: 'Safety protocols verified for livestock facility access', location: 'Prairie Ranch Facility', verifier: 'Safety Supervisor', responseTime: 1.6 },
      { id: '7', timestamp: new Date(now.getTime() - 72 * 60 * 1000), type: 'agent_verification', category: 'Franchise', agentName: 'Franchisee System Access', credentialTitle: 'Franchise Agreement', result: 'success', details: 'Business portal access granted for new franchise location', location: 'FastBurger Corporate Portal', verifier: 'Franchise Support Team', responseTime: 3.2 },
      { id: '8', timestamp: new Date(now.getTime() - 89 * 60 * 1000), type: 'credential_verification', category: 'Hunting', credentialTitle: 'Hunting License', result: 'failed', details: 'Verification failed - Conservation stamp missing for current season', location: 'North Ridge Hunting Grounds', verifier: 'DNR Officer', error: 'Missing Conservation Stamp' },
      { id: '9', timestamp: new Date(now.getTime() - 105 * 60 * 1000), type: 'policy_evaluation', category: 'Education', policyName: 'Library Resource Access', credentialTitle: 'Student ID', result: 'success', details: 'Graduate research collection access approved', location: 'University Main Library', verifier: 'Reference Librarian', responseTime: 0.9 },
      { id: '10', timestamp: new Date(now.getTime() - 123 * 60 * 1000), type: 'agent_verification', category: 'Real Estate', agentName: 'Property Closing Verification', credentialTitle: 'E&O Insurance', result: 'success', details: 'Insurance coverage verified for $750,000 property closing', location: 'Elite Realty Office', verifier: 'Closing Agent', responseTime: 2.3 },
      { id: '11', timestamp: new Date(now.getTime() - 145 * 60 * 1000), type: 'credential_shared', category: 'Healthcare', credentialTitle: 'CPR Certification', result: 'success', details: 'Emergency response credential shared for volunteer position', location: 'Online Application', verifier: 'Red Cross Volunteer Coordinator', sharedVia: 'Email Attachment' },
      { id: '12', timestamp: new Date(now.getTime() - 167 * 60 * 1000), type: 'policy_evaluation', category: 'Franchise', policyName: 'Territory Manager Authorization', credentialTitle: 'Management Certificate', result: 'success', details: 'Regional expansion authorization approved', location: 'Corporate Headquarters', verifier: 'Regional Director', responseTime: 2.8 }
    );

    return logs.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
  };

  const activityLogs = generateRecipientActivityLogs();

  // Calculate activity statistics
  const activityStats = {
    totalInteractions: activityLogs.length,
    successfulInteractions: activityLogs.filter(log => log.result === 'success').length,
    recentInteractions: activityLogs.filter(log => new Date().getTime() - log.timestamp.getTime() < 24 * 60 * 60 * 1000).length,
    avgResponseTime: activityLogs.filter(log => log.responseTime).reduce((sum, log) => sum + (log.responseTime || 0), 0) / activityLogs.filter(log => log.responseTime).length || 0
  };
  */

  return (
    <div className="min-h-screen bg-cool-gray-100">
      {/* Header */}
      <header className="credential-capital-header shadow-sm">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="bg-white/10 p-2 rounded-lg">
                <Shield className="h-8 w-8 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-display font-bold text-white">
                  My Credentials
                </h1>
                <p className="text-blue-100">
                  Digital Credential Wallet
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={refreshCredentials}
                disabled={loading}
                className="credential-capital-button-header"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                {loading ? 'Refreshing...' : 'Refresh'}
              </Button>
              <span className="text-blue-100">Welcome, {user?.name}</span>
              <Button variant="outline" size="sm" className="credential-capital-button-header">
                <Bell className="h-4 w-4 mr-2" />
                Notifications
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={logout}
                className="credential-capital-button-header"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Sign Out
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="p-6">
        {/* Credentials Grid - Only showing credentials list as requested */}
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-electric-steel mx-auto mb-4"></div>
            <p className="text-warm-slate">Loading your credentials...</p>
                    </div>
        ) : error ? (
          <div className="text-center py-8">
            <div className="bg-red-50 border border-red-200 rounded-lg p-6">
              <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-red-800 mb-2">Error Loading Credentials</h3>
              <p className="text-red-600 mb-4">{error}</p>
              <Button onClick={refreshCredentials} className="bg-red-600 hover:bg-red-700">
                Try Again
              </Button>
            </div>
                  </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredCredentials.map((credential) => {
              // Use real template data if available, otherwise fallback to basic template
              const template = realTemplate && credential.template_file_id === realTemplate.id 
                ? realTemplate 
                : getCredentialTemplate(credential);
              
              // Show loading state if template is still loading
              if (templateLoading && credential.template_file_id) {
                return (
                  <Card key={credential.id} className="credential-capital-card">
                    <CardContent className="pt-6 text-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-electric-steel mx-auto mb-4"></div>
                      <p className="text-warm-slate">Loading template...</p>
                    </CardContent>
                  </Card>
                );
              }
                
                return (
                  <Card key={credential.id} className="credential-capital-card hover:shadow-lg transition-shadow">
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div className="flex items-center space-x-3 flex-1 min-w-0">
                          <span className="text-2xl flex-shrink-0">{credential.issuerLogo}</span>
                          <div className="min-w-0 flex-1">
                            <CardTitle className="text-lg text-charcoal-ink line-clamp-2 leading-tight">{credential.title}</CardTitle>
                            <CardDescription className="text-warm-slate truncate">{credential.issuer}</CardDescription>
                          </div>
                        </div>
                        <Badge variant={credential.status === 'active' ? 'default' : 'secondary'} className="flex-shrink-0 ml-2">
                          {credential.status}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        <div className="flex items-center justify-between text-sm text-warm-slate">
                          <span>Issued: {credential.issuedDate}</span>
                          {credential.expiryDate && (
                            <span>Expires: {credential.expiryDate}</span>
                          )}
                        </div>
                        
                        <div className="flex items-center justify-between text-sm text-warm-slate">
                          <span>{credential.verificationCount} verifications</span>
                          <Badge variant="outline" className="text-warm-slate">{credential.category}</Badge>
                        </div>

                        <div className="flex space-x-2">
                          <Dialog>
                            <DialogTrigger asChild>
                              <Button variant="outline" size="sm" className="flex-1">
                                <Eye className="h-4 w-4 mr-1" />
                                View
                              </Button>
                            </DialogTrigger>
                            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                              <DialogHeader>
                                <DialogTitle className="flex items-center space-x-2 text-charcoal-ink">
                                  <span>{credential.issuerLogo}</span>
                                  <span>{credential.title}</span>
                                </DialogTitle>
                                <DialogDescription className="text-warm-slate">
                                  Issued by {credential.issuer} on {credential.issuedDate}
                                </DialogDescription>
                              </DialogHeader>
                              <div className="space-y-6 bg-white p-6 rounded-lg">
                                {/* Large PDF Preview */}
                                {template ? (
                                  <div className="flex justify-center">
                                    <div className="border border-gray-300 rounded-lg overflow-hidden shadow-lg">
                                      <CredentialPreview 
                                        credential={credential} 
                                        template={template}
                                      />
                                    </div>
                                  </div>
                                ) : (
                                  <div className="text-center p-8 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50">
                                    <FileText className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                                    <p className="text-gray-600">PDF template not available</p>
                                    <p className="text-sm text-gray-500">This credential was issued without a visual template</p>
                                  </div>
                                )}

                                {/* Credential Details */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-50 p-4 rounded-lg">
                                  <div className="space-y-4">
                                    <h4 className="font-semibold text-charcoal-ink">Credential Information</h4>
                                    {Object.entries(credential.fields).slice(0, Math.ceil(Object.keys(credential.fields).length / 2)).map(([key, value]) => (
                                      <div key={key} className="p-3 bg-white rounded-lg border border-gray-200">
                                        <span className="font-medium text-warm-slate block text-sm">{key}:</span>
                                        <span className="text-charcoal-ink">{value}</span>
                                      </div>
                                    ))}
                                  </div>
                                  <div className="space-y-4">
                                    <h4 className="font-semibold text-charcoal-ink">Additional Details</h4>
                                    {Object.entries(credential.fields).slice(Math.ceil(Object.keys(credential.fields).length / 2)).map(([key, value]) => (
                                      <div key={key} className="p-3 bg-white rounded-lg border border-gray-200">
                                        <span className="font-medium text-warm-slate block text-sm">{key}:</span>
                                        <span className="text-charcoal-ink">{value}</span>
                                      </div>
                                    ))}
                                    <div className="p-3 bg-white rounded-lg border border-gray-200">
                                        <span className="font-medium text-warm-slate block text-sm">Status:</span>
                                        <Badge variant={credential.status === 'active' ? 'default' : 'secondary'}>
                                          {credential.status}
                                        </Badge>
                                      </div>
                                  </div>
                                </div>

                                {/* Action Buttons */}
                                <div className="flex justify-center space-x-4 pt-4 border-t border-gray-200 bg-gray-50 p-4 rounded-lg">
                                  <Button 
                                    variant="outline" 
                                    className="credential-capital-button-secondary"
                                    onClick={() => downloadCredential(credential.id)}
                                  >
                                    <Download className="h-4 w-4 mr-2" />
                                    Download PDF
                                  </Button>
                                  <Button 
                                    variant="outline" 
                                    className="credential-capital-button-secondary"
                                    onClick={() => openInNewTab(credential.id)}
                                  >
                                    <ExternalLink className="h-4 w-4 mr-2" />
                                    Open in New Tab
                                  </Button>
                                </div>
                              </div>
                            </DialogContent>
                          </Dialog>

                          <Dialog>
                            <DialogTrigger asChild>
                              <Button size="sm" className="flex-1 credential-capital-button-primary">
                                <Share2 className="h-4 w-4 mr-1" />
                                Share
                              </Button>
                            </DialogTrigger>
                            <DialogContent className="max-w-md">
                              <DialogHeader>
                                <DialogTitle className="text-charcoal-ink">Share Credential</DialogTitle>
                                <DialogDescription className="text-warm-slate">
                                  Share {credential.title} with verifiers
                                </DialogDescription>
                              </DialogHeader>
                              <div className="space-y-4">
                                <div className="space-y-2">
                                  <label className="text-sm font-medium text-charcoal-ink">Verification URL</label>
                                  <div className="flex space-x-2">
                                    <input 
                                      type="text" 
                                      value={generateShareUrl(credential.id)}
                                      readOnly
                                      className="flex-1 px-3 py-2 border rounded-md text-sm credential-capital-input"
                                    />
                                    <Button size="sm" variant="outline">
                                      <Link className="h-4 w-4" />
                                    </Button>
                                  </div>
                                </div>
                                
                                <div className="text-center">
                                  <p className="text-sm font-medium mb-2 text-charcoal-ink">QR Code</p>
                                  <div className="flex justify-center">
                                    <img 
                                      src={generateQRCode(generateShareUrl(credential.id))}
                                      alt="QR Code"
                                      className="w-32 h-32"
                                    />
                                  </div>
                                </div>

                                <div className="flex space-x-2">
                                  <Button 
                                    variant="outline" 
                                    className="flex-1"
                                    onClick={() => shareOnLinkedIn(credential.id)}
                                  >
                                    <Mail className="h-4 w-4 mr-2" />
                                    LinkedIn
                                  </Button>
                                  <Button 
                                    variant="outline" 
                                    className="flex-1"
                                    onClick={() => downloadCredential(credential.id)}
                                  >
                                    <Download className="h-4 w-4 mr-2" />
                                    Download PDF
                                  </Button>
                                </div>
                              </div>
                            </DialogContent>
                          </Dialog>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {filteredCredentials.length === 0 && (
              <Card className="credential-capital-card">
                <CardContent className="pt-6 text-center">
                  <p className="text-warm-slate">No credentials found matching your search criteria.</p>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
    </div>
  );
}